// YOUR WORK — the first of the welcome's three choices on the setup surface
// (components/OnboardingWizard.js; functions/onboardingPresets.js; wt/presets
// CONTRACT §1: "At most ONE screen per choice; every choice has a visible
// default; plain words + app icons, never a feature table"). An industry IS a
// Workspace Preset; "Something else" is a custom workspace.
//
// The wizard's own parts: the step extends OnboardingWizardStep, the tiles
// OnboardingTile (toggle buttons with `aria-pressed`, the plain tick when
// pressed). Surfaces differ by fill, never by a border. Every value comes
// from functions/onboardingPresets.js through `el.call`; every factory names
// the root keys it follows (`…Deps`), so it re-runs when the registry, the
// catalog or the apply lands.

// YOUR WORK — the featured presets and Something else. While the registry is
// read: one calm line; after a failed read: the line and Try again (Something
// else stays). A workspace set up already, or this flow's own apply, shows ONE
// row instead — what it is set up for, ✓ once done — and Continue.
export const OnboardingWorkStep = {
  extends: 'OnboardingWizardStep',
  wizardStep: 'work',

  // THE FIRST WORKSPACE'S NAME (round 2 BLOCKER #5, elena-r2 + sam-r2): a
  // direct sign-up names the workspace it already has — the one with the
  // sign-up credits — instead of a "Create team's workspace" dialog that made
  // a second, empty one. Shows the stored name until the person types; the
  // name is written when Your work is left forward or the welcome closes
  // (functions/onboardingPresets.js onboardingNameSave). Enter goes on, like
  // the idea field.
  AppField: {
    label: (el) => el.call('polyglot', 'workspace.createOrg.nameLabel'),
    AppInput: {
      type: 'text',
      name: 'onboardingWorkspaceName',
      autocomplete: 'organization',
      maxlength: 60,
      enterkeyhint: 'next',
      ariaLabel: (el) => el.call('polyglot', 'workspace.createOrg.nameLabel'),
      valueDeps: [
        (el, s) => s.root && s.root._onboardingFlow,
        (el, s) => s.root && s.root.workspace
      ],
      value: (el, s) => el.call('onboardingNameOf', s.root) || '',
      onInput: (e, el) => el.call('onboardingNameSet', (e.target && e.target.value) || ''),
      onKeydown: (e, el) => {
        if (e.key !== 'Enter' || e.isComposing) return
        e.preventDefault()
        e.stopPropagation()
        el.call('onboardingWizardGo', 1)
      }
    }
  },

  OnboardingWorkStatus: {
    hideDeps: [
      (el, s) => s.root && s.root._onboardingFlow,
      (el, s) => s.root && s.root._presetApply,
      (el, s) => s.root && s.root.workspace
    ],
    hide: (el, s) => (el.call('onboardingWorkView', s.root) || {}).state === 'tiles'
  },

  WorkNote: {
    tag: 'p',
    role: 'status',
    margin: '0',
    fontSize: 'Z',
    lineHeight: '1.5',
    color: 'inactive',
    hideDeps: [
      (el, s) => s.root && s.root._workspacePresets,
      (el, s) => s.root && s.root._workspacePresetsError
    ],
    hide: (el, s) => {
      const view = el.call('onboardingWorkView', s.root) || {}
      return view.state !== 'tiles' || !(view.loading || view.failed)
    },
    textDeps: [(el, s) => s.root && s.root._workspacePresetsError],
    text: (el, s) =>
      el.call(
        'polyglot',
        (el.call('onboardingWorkView', s.root) || {}).failed
          ? 'marketplace.preset.loadFailed'
          : 'workspace.onboarding.work.loading'
      )
  },

  AppCapsuleBtnGhost: {
    compact: true,
    alignSelf: 'flex-start',
    hideDeps: [
      (el, s) => s.root && s.root._workspacePresets,
      (el, s) => s.root && s.root._workspacePresetsError
    ],
    hide: (el, s) => !(el.call('onboardingWorkView', s.root) || {}).failed,
    text: '{{ marketplace.preset.tryAgain | polyglot }}',
    onClick: (e, el) => {
      e.stopPropagation()
      el.call('workspacePresetsLoad', true)
    }
  },

  // Two columns (2 × 2: three featured presets and Something else, then the
  // more tile across both) — wide enough for a name on one line, symmetric;
  // one column on a phone. "More kinds of work" opens the rest of the
  // registry in the same grid (an even count again: the registry's ten).
  OnboardingTileGrid: {
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    '@mobileL': { gridTemplateColumns: 'minmax(0, 1fr)' },
    hideDeps: [(el, s) => s.root && s.root._onboardingFlow],
    hide: (el, s) => (el.call('onboardingWorkView', s.root) || {}).state !== 'tiles',
    childExtends: 'OnboardingWorkTile',
    childrenDeps: [
      (el, s) => s.root && s.root._onboardingFlow,
      (el, s) => s.root && s.root._workspacePresets
    ],
    children: (el, s) => el.call('onboardingWorkTiles', s.root) || []
  }
}

// ONE WORK — a preset (its apps' icons, its name, its one line) or Something
// else. A press picks it; pressing the picked one again takes it back. The
// more tile ("More kinds of work") is no pick: it spans both columns, says
// it opens more (`aria-expanded`, never `aria-pressed`) and its press shows
// the rest of the registry.
export const OnboardingWorkTile = {
  extends: 'OnboardingTile',
  isMore: (el, s) => !!(s && s.more),
  '.isMore': { gridColumn: '1 / -1' },
  ariaPressedDeps: [(el, s) => s.root && s.root._onboardingFlow],
  ariaPressed: (el, s) => (s && s.more ? null : s && s.picked ? 'true' : 'false'),
  ariaExpanded: (el, s) => (s && s.more ? 'false' : null),
  onClick: (e, el, s) => {
    e.stopPropagation()
    if (s && s.more) el.call('onboardingWorkMore')
    else if (s && s.value) el.call('onboardingWorkPick', s.value, { toggle: true })
  },
  // The icon row ends before the pick mark's corner (OnboardingTile's
  // TileCheck: `right: 'Y'`, `boxSize: 'B'`, over the tile's `A` padding), so
  // no icon sits under the mark — reserved always, pressed or not.
  WorkGlyphs: {
    order: '-1',
    display: 'flex',
    flexWrap: 'wrap',
    gap: 'X',
    minWidth: '0',
    paddingRight: 'B',
    ariaHidden: 'true',
    childExtends: 'OnboardingAppGlyph',
    childrenAs: 'state',
    childrenDeps: [
      (el, s) => s.root && s.root._wsAppsCatalog,
      (el, s) => s.root && s.root._workspacePresets
    ],
    children: (el, s) => el.call('onboardingWorkGlyphs', s && s.value, s && s.root) || []
  }
}

// An app's calm glyph at a tile's size — the App strip's own tile
// (AppGlyphTile: the `layer` wash, `title` ink, the app's own icon), one step
// smaller. Decorative: the row or the tile names the app in words.
export const OnboardingAppGlyph = {
  extends: 'AppGlyphTile',
  boxSize: 'avatarXs',
  minWidth: 'avatarXs',
  ariaHidden: 'true',
  title: (el, s) => (s && s.title) || null,
  Icon: { boxSize: 'iconSm' }
}

// What the workspace is set up for — or is being set up for — in one row:
// the plain tick once done, the preset's name, its one line.
export const OnboardingWorkStatus = {
  role: 'status',
  display: 'flex',
  alignItems: 'center',
  gap: 'Z',
  minWidth: '0',
  boxSizing: 'border-box',
  padding: 'A',
  round: 'radiusCard',
  theme: 'layer',
  color: 'title',
  StatusMark: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: '0',
    boxSize: 'B',
    round: 'radiusPill',
    theme: 'primary',
    ariaHidden: 'true',
    hideDeps: [
      (el, s) => s.root && s.root._presetApply,
      (el, s) => s.root && s.root._onboardingFlow
    ],
    hide: (el, s) => !(el.call('onboardingWorkView', s.root) || {}).done,
    Icon: { name: 'checkmark', boxSize: 'iconSm' }
  },
  StatusWords: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'W',
    minWidth: '0',
    StatusTitle: {
      tag: 'span',
      fontSize: 'Z2',
      fontWeight: '500',
      lineHeight: '1.35',
      color: 'title',
      overflowWrap: 'break-word',
      textDeps: [
        (el, s) => s.root && s.root._presetApply,
        (el, s) => s.root && s.root._onboardingFlow,
        (el, s) => s.root && s.root.workspace
      ],
      text: (el, s) => (el.call('onboardingWorkView', s.root) || {}).title || ''
    },
    StatusHint: {
      tag: 'span',
      fontSize: 'Z',
      lineHeight: '1.45',
      color: 'inactive',
      overflowWrap: 'break-word',
      hideDeps: [(el, s) => s.root && s.root._onboardingFlow],
      hide: (el, s) => !(el.call('onboardingWorkView', s.root) || {}).hint,
      textDeps: [(el, s) => s.root && s.root._onboardingFlow],
      text: (el, s) => (el.call('onboardingWorkView', s.root) || {}).hint || ''
    }
  }
}
